import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Flame, Award, Circle, RefreshCw, AlertTriangle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useStreakStats from '../../hooks/UseStreakStatsResult';
import useRefreshLimiter from '../../hooks/useRefreshLimiter';

// Configuración completa de colores
const COLOR_VARIANTS = {
    blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/50',
        text: 'text-blue-600 dark:text-blue-400',
        icon: 'text-blue-500 dark:text-blue-300',
        progressBg: 'bg-blue-200 dark:bg-blue-800',
        progressFill: 'bg-blue-600 dark:bg-blue-400'
    },
    purple: {
        bg: 'bg-purple-50 dark:bg-purple-900/50',
        text: 'text-purple-600 dark:text-purple-400',
        icon: 'text-purple-500 dark:text-purple-300',
        progressBg: 'bg-purple-200 dark:bg-purple-800',
        progressFill: 'bg-purple-600 dark:bg-purple-400'
    },
    green: {
        bg: 'bg-green-50 dark:bg-green-900/50',
        text: 'text-green-600 dark:text-green-400',
        icon: 'text-green-500 dark:text-green-300',
        progressBg: 'bg-green-200 dark:bg-green-800',
        progressFill: 'bg-green-600 dark:bg-green-400'
    },
    orange: {
        bg: 'bg-orange-50 dark:bg-orange-900/50',
        text: 'text-orange-600 dark:text-orange-400',
        icon: 'text-orange-500 dark:text-orange-300',
        progressBg: 'bg-orange-200 dark:bg-orange-800',
        progressFill: 'bg-orange-600 dark:bg-orange-400'
    },
    red: {
        bg: 'bg-red-50 dark:bg-red-900/50',
        text: 'text-red-600 dark:text-red-400',
        icon: 'text-red-500 dark:text-red-300',
        progressBg: 'bg-red-200 dark:bg-red-800',
        progressFill: 'bg-red-600 dark:bg-red-400'
    }
};

export type ColorVariant = keyof typeof COLOR_VARIANTS;

// Tipo para los niveles de racha personalizables
export interface StreakLevel {
    threshold: number;
    label: string;
}

export interface StreakStatsProps {
    color?: ColorVariant;
    timeRange?: 'week' | 'month' | 'year' | 'all';
    autoRefresh?: boolean;
    refreshInterval?: number;
    // Configuración visual
    streakLevels?: StreakLevel[];
    animation?: {
        enabled?: boolean;
        duration?: number;
    };
    // Funciones
    onStreakClick?: () => void;
    onDetailsClick?: () => void;
    className?: string;
    customIcon?: React.ReactNode;
    // Configuración del limitador de refresco
    refreshCooldown?: number;
    maxRefreshCount?: number;
    refreshTimeWindow?: number;
}

export function StreakStats({
    color = 'red',
    timeRange = 'all',
    autoRefresh = false,
    refreshInterval,
    streakLevels,
    animation = { enabled: true, duration: 0.3 },
    onStreakClick,
    onDetailsClick,
    className = '',
    customIcon,
    // Valores predeterminados para el limitador de refresco
    refreshCooldown = 5000,     // 5 segundos de cooldown
    maxRefreshCount = 3,        // máximo 3 refrescos
    refreshTimeWindow = 30000   // en un periodo de 30 segundos
}: StreakStatsProps) {
    const { t } = useTranslation();
    const colorVariant = COLOR_VARIANTS[color];

    // Usar el hook de estadísticas de racha
    const {
        streakData,
        isLoading,
        error,
        refreshStreakData
    } = useStreakStats({
        timeRange,
        autoRefresh,
        refreshInterval
    });

    // Usar nuestro hook de limitación de refresco
    const {
        isDisabled,
        hasReachedLimit,
        cooldownRemaining,
        executeRefresh
    } = useRefreshLimiter({
        cooldownTime: refreshCooldown,
        maxRefreshCount: maxRefreshCount,
        timeWindow: refreshTimeWindow
    });

    // Manejar el refresco con limitación
    const handleRefresh = (e: React.MouseEvent) => {
        e.stopPropagation();
        executeRefresh(refreshStreakData);
    };

    // Formatear el tiempo de enfriamiento restante
    const formatCooldownTime = () => {
        if (cooldownRemaining <= 0) return '';
        return `${Math.ceil(cooldownRemaining / 1000)}s`;
    };

    // Niveles de racha predeterminados
    const defaultStreakLevels: StreakLevel[] = useMemo(() => [
        { threshold: 0, label: t('streakStats.levels.beginner') },
        { threshold: 7, label: t('streakStats.levels.consistent') },
        { threshold: 30, label: t('streakStats.levels.dedicated') },
        { threshold: 90, label: t('streakStats.levels.master') }
    ], [t]);

    // Usar niveles personalizados o los predeterminados
    const levels = streakLevels || defaultStreakLevels;

    // Determinar el nivel de racha actual basado en los niveles configurados
    const getStreakLevel = () => {
        if (!streakData) return levels[0].label;

        // Ordenar niveles de mayor a menor para encontrar el primer umbral que el usuario ha superado
        const sortedLevels = [...levels].sort((a, b) => b.threshold - a.threshold);
        for (const level of sortedLevels) {
            if (streakData.currentStreak >= level.threshold) {
                return level.label;
            }
        }
        return levels[0].label; // Nivel más bajo por defecto
    };

    // Calcular el porcentaje de progreso para la barra
    const progressPercentage = useMemo(() => {
        if (!streakData || streakData.longestStreak === 0) return 0;
        return Math.min(Math.max((streakData.currentStreak / streakData.longestStreak) * 100, 0), 100);
    }, [streakData]);

    // Calcular días desde la última tarea completada
    const daysSinceLastCompleted = useMemo(() => {
        if (!streakData || !streakData.lastCompletedDate) return null;
        const lastCompletedDate = new Date(streakData.lastCompletedDate);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - lastCompletedDate.getTime());
        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }, [streakData]);

    // Renderizar estado de error
    if (error && !streakData) {
        return (
            <div className={`
        bg-white dark:bg-gray-800 
        rounded-xl shadow-lg
        border border-gray-100 dark:border-gray-700
        p-5 flex items-center
        ${className}
      `}>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="ml-2 text-red-500">{t('common.error')}</span>
            </div>
        );
    }

    // Valores predeterminados para cuando no hay datos
    const currentStreak = streakData?.currentStreak || 0;
    const longestStreak = streakData?.longestStreak || 0;

    return (
        <motion.div
            initial={animation.enabled ? { opacity: 0, y: 10 } : undefined}
            animate={animation.enabled ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: animation.duration }}
            onClick={onStreakClick}
            className={`
        bg-white dark:bg-gray-800 
        rounded-xl shadow-lg hover:shadow-xl 
        transition-all duration-300 
        border border-gray-100 dark:border-gray-700
        p-5 space-y-3
        relative
        ${onStreakClick ? 'cursor-pointer' : ''}
        ${className}
      `}
        >
            {/* Loading state */}
            {isLoading && (
                <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center z-10 rounded-xl">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                        <RefreshCw className="w-6 h-6 text-gray-400" />
                    </motion.div>
                </div>
            )}

            {/* Encabezado */}
            <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('streakStats.title')}
                </h3>
                <div className="flex items-center space-x-2">
                    {/* Botón de refresco con limitador */}
                    <div className="relative">
                        <motion.button
                            whileHover={isDisabled || isLoading ? undefined : { scale: 1.1 }}
                            whileTap={isDisabled || isLoading ? undefined : { rotate: 180, transition: { duration: 0.3 } }}
                            onClick={handleRefresh}
                            disabled={isDisabled || isLoading}
                            className={`p-1 rounded-full ${isDisabled
                                ? hasReachedLimit
                                    ? 'text-red-400 cursor-not-allowed'
                                    : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            title={
                                hasReachedLimit
                                    ? t('streakStats.tooManyRefreshes') || 'Demasiados refrescos'
                                    : isDisabled
                                        ? t('streakStats.waitToRefresh', { time: formatCooldownTime() }) || `Espera ${formatCooldownTime()}`
                                        : t('streakStats.refreshData') || 'Refrescar datos'
                            }
                        >
                            {hasReachedLimit ? (
                                <XCircle className="w-3.5 h-3.5" />
                            ) : (
                                <RefreshCw className="w-3.5 h-3.5" />
                            )}
                        </motion.button>

                        {/* Indicador de enfriamiento */}
                        {isDisabled && !hasReachedLimit && cooldownRemaining > 0 && (
                            <span className="absolute -top-1 -right-1 text-[8px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full px-1 min-w-4 text-center">
                                {Math.ceil(cooldownRemaining / 1000)}
                            </span>
                        )}
                    </div>
                    {customIcon || <Flame className={`w-5 h-5 ${colorVariant.icon}`} />}
                </div>
            </div>

            {/* Racha actual */}
            <div className="flex items-baseline space-x-2">
                <p className={`
          text-3xl font-bold 
          text-gray-900 dark:text-white
          ${colorVariant.text}
        `}>
                    {currentStreak}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('streakStats.days')}
                </p>
            </div>

            {/* Detalles de la racha */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Award className={`w-4 h-4 ${colorVariant.icon}`} />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('streakStats.longestStreak')}
                        </span>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {longestStreak} {t('streakStats.days')}
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Circle className={`w-4 h-4 ${colorVariant.icon}`} />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('streakStats.consistencyLevel')}
                        </span>
                    </div>
                    <span className={`
            text-sm font-medium 
            ${colorVariant.text}
          `}>
                        {getStreakLevel()}
                    </span>
                </div>

                {daysSinceLastCompleted !== null && daysSinceLastCompleted > 0 && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <AlertTriangle className={`w-4 h-4 ${daysSinceLastCompleted === 1 ? 'text-yellow-500' : 'text-red-500'
                                }`} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t('streakStats.lastActivity')}
                            </span>
                        </div>
                        <span className={`
              text-sm font-medium 
              ${daysSinceLastCompleted === 1 ? 'text-yellow-500' : 'text-red-500'}
            `}>
                            {daysSinceLastCompleted} {t('streakStats.daysAgo')}
                        </span>
                    </div>
                )}
            </div>

            {/* Barra de progreso para la racha */}
            <div className={`h-2 w-full rounded-full ${colorVariant.progressBg} overflow-hidden`}>
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.5, type: 'spring' }}
                    className={`h-full ${colorVariant.progressFill}`}
                />
            </div>

            {/* Botón de detalles opcional */}
            {onDetailsClick && (
                <div
                    className="text-center pt-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDetailsClick();
                    }}
                >
                    <button className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        {t('streakStats.viewDetails')}
                    </button>
                </div>
            )}

            {/* Mensaje de límite alcanzado */}
            {hasReachedLimit && (
                <div className="text-[10px] text-red-500 dark:text-red-400 text-center mt-1">
                    {t('streakStats.tooManyRefreshes')}
                </div>
            )}
        </motion.div>
    );
}

export default StreakStats;